import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Percent, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DiscountForm({ code, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    code: code?.code || '',
    discount_type: code?.discount_type || 'percentage',
    discount_value: code?.discount_value?.toString() || '',
    expiry_date: code?.expiry_date ? code.expiry_date.split('T')[0] : '',
    is_active: code?.is_active !== false,
    show_in_banner: code?.show_in_banner || false,
    banner_text: code?.banner_text || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      code: formData.code.toUpperCase(),
      discount_value: parseFloat(formData.discount_value),
      expiry_date: formData.expiry_date ? formData.expiry_date + 'T23:59:59Z' : null
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white">{code ? 'Edit' : 'Create'} Discount Code</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-gray-300">Code</Label>
            <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="SAVE10" className="bg-gray-700 border-gray-600 text-white uppercase" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Type</Label>
              <Select value={formData.discount_type} onValueChange={(v) => setFormData({ ...formData, discount_type: v })}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="percentage" className="text-white">Percentage (%)</SelectItem>
                  <SelectItem value="fixed" className="text-white">Fixed (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Value</Label>
              <Input type="number" step="0.01" value={formData.discount_value} onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })} className="bg-gray-700 border-gray-600 text-white" required />
            </div>
          </div>
          <div>
            <Label className="text-gray-300">Expiry Date</Label>
            <Input type="date" value={formData.expiry_date} onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })} className="bg-gray-700 border-gray-600 text-white" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-gray-300"><input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />Active</label>
            <label className="flex items-center gap-2 text-gray-300"><input type="checkbox" checked={formData.show_in_banner} onChange={(e) => setFormData({ ...formData, show_in_banner: e.target.checked })} />Show in Banner</label>
          </div>
          {formData.show_in_banner && (
            <div>
              <Label className="text-gray-300">Banner Text</Label>
              <Textarea value={formData.banner_text} onChange={(e) => setFormData({ ...formData, banner_text: e.target.value })} placeholder="Use code SAVE10 for 10% off!" rows={2} className="bg-gray-700 border-gray-600 text-white" />
            </div>
          )}
          <div className="flex gap-4 pt-4 border-t border-gray-700">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-gray-600">Cancel</Button>
            <Button type="submit" className="flex-1 bg-[#F54021] hover:bg-[#ff5a35]">{code ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DiscountCard({ code, onEdit, onDelete, onToggleBanner }) {
  const isExpired = code.expiry_date && new Date(code.expiry_date) < new Date();
  const usageCount = code.used_by_emails?.length || 0;

  return (
    <div className={`bg-gray-800 rounded-xl border p-6 ${!code.is_active || isExpired ? 'border-gray-700 opacity-60' : 'border-gray-600'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${code.discount_type === 'percentage' ? 'bg-purple-500/20' : 'bg-green-500/20'}`}>
            <Percent className={`w-6 h-6 ${code.discount_type === 'percentage' ? 'text-purple-400' : 'text-green-400'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold text-white font-mono">{code.code}</h3>
              {!code.is_active && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Inactive</span>}
              {isExpired && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">Expired</span>}
              {code.show_in_banner && <span className="text-xs bg-[#d4af37]/20 text-[#d4af37] px-2 py-0.5 rounded">In Banner</span>}
            </div>
            <p className="text-gray-400 mt-1">
              {code.discount_type === 'percentage' ? `${code.discount_value}%` : `£${code.discount_value}`} off
              {code.expiry_date && <span className="ml-2 text-gray-500">• Expires {new Date(code.expiry_date).toLocaleDateString()}</span>}
            </p>
            {code.banner_text && <p className="text-sm text-[#d4af37] mt-1">"{code.banner_text}"</p>}
            {usageCount > 0 && <p className="text-xs text-gray-500 mt-1">Used {usageCount} time{usageCount !== 1 ? 's' : ''}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onToggleBanner(code)} 
            className={`border-gray-600 relative group ${code.show_in_banner ? 'bg-[#d4af37]/20' : ''}`}
            title={code.show_in_banner ? 'Hide from banner' : 'Show in banner'}
          >
            {code.show_in_banner ? <Eye className="w-4 h-4 text-[#d4af37]" /> : <EyeOff className="w-4 h-4" />}
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {code.show_in_banner ? 'Hide from banner' : 'Show in banner'}
            </span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(code)} className="border-gray-600"><Pencil className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(code.id)} className="border-red-600 text-red-400 hover:bg-red-900/30"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDiscounts() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/discount-codes`);
      setCodes(response.data);
    } catch (error) { toast.error('Failed to fetch'); }
    finally { setLoading(false); }
  };

  const handleSave = async (data) => {
    try {
      if (editingCode) {
        await axios.put(`${API}/admin/discount-codes/${editingCode.id}`, data);
        toast.success('Code updated');
      } else {
        await axios.post(`${API}/admin/discount-codes`, data);
        toast.success('Code created');
      }
      fetchCodes();
      setShowForm(false);
      setEditingCode(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${id}`);
      toast.success('Deleted');
      fetchCodes();
    } catch (error) { toast.error('Failed'); }
  };

  const handleToggleBanner = async (code) => {
    try {
      await axios.put(`${API}/admin/discount-codes/${code.id}`, { show_in_banner: !code.show_in_banner });
      toast.success(code.show_in_banner ? 'Removed from banner' : 'Added to banner');
      fetchCodes();
    } catch (error) { toast.error('Failed'); }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Discount Codes</h2>
          <p className="text-gray-400 text-sm">Create promotional codes and banner offers</p>
        </div>
        <Button onClick={() => { setEditingCode(null); setShowForm(true); }} className="bg-[#F54021] hover:bg-[#ff5a35]"><Plus className="w-4 h-4 mr-2" />Create</Button>
      </div>
      {showForm && <DiscountForm code={editingCode} onSave={handleSave} onCancel={() => { setShowForm(false); setEditingCode(null); }} />}
      <div className="space-y-4">
        {codes.map((c) => <DiscountCard key={c.id} code={c} onEdit={(code) => { setEditingCode(code); setShowForm(true); }} onDelete={handleDelete} onToggleBanner={handleToggleBanner} />)}
      </div>
      {codes.length === 0 && <div className="text-center py-12 text-gray-400">No discount codes yet.</div>}
    </div>
  );
}
